(function () {
  'use strict';

  function parseJSON(str, fallback) {
    if (!str) return fallback;
    try {
      return JSON.parse(str);
    } catch (e) {
      return fallback;
    }
  }

  var UID = 0;
  // No per-session preview cap — shoppers / QA may regenerate freely.
  var SESSION_LIMIT = Number.POSITIVE_INFINITY;
  // Mobile-first allowlist: phone photos are usually JPG or HEIC/HEIF; PNG kept for screenshots.
  var ALLOWED_EXTS = { jpg: 1, jpeg: 1, png: 1, heic: 1, heif: 1 };
  var ALLOWED_MIME = {
    'image/jpeg': 'jpg',
    'image/jpg': 'jpg',
    'image/pjpeg': 'jpg',
    'image/png': 'png',
    'image/heic': 'heic',
    'image/heif': 'heif',
    'image/heic-sequence': 'heic',
    'image/heif-sequence': 'heif',
  };
  var heic2anyPromise = null;
  var GLOBO_FILE_LABELS = [
    "Upload your pet's photo",
    'Second pet photo',
    'Upload your photo',
    'Pet photo',
  ];

  function fileExt(name) {
    var m = String(name || '')
      .toLowerCase()
      .match(/\.([a-z0-9]+)$/);
    return m ? m[1] : '';
  }

  function detectFormat(file) {
    if (!file) return null;
    var mime = String(file.type || '').toLowerCase().trim();
    if (ALLOWED_MIME[mime]) return ALLOWED_MIME[mime];
    var ext = fileExt(file.name);
    if (ALLOWED_EXTS[ext]) return ext;
    return null;
  }

  function isHeicFormat(fmt) {
    return fmt === 'heic' || fmt === 'heif';
  }

  function loadHeic2Any(src) {
    if (window.heic2any) return Promise.resolve(window.heic2any);
    if (heic2anyPromise) return heic2anyPromise;
    if (!src) return Promise.reject(new Error('heic2any_missing'));
    heic2anyPromise = new Promise(function (resolve, reject) {
      var s = document.createElement('script');
      s.src = src;
      s.async = true;
      s.onload = function () {
        if (window.heic2any) resolve(window.heic2any);
        else reject(new Error('heic2any_load_failed'));
      };
      s.onerror = function () {
        heic2anyPromise = null;
        reject(new Error('heic2any_load_failed'));
      };
      document.head.appendChild(s);
    });
    return heic2anyPromise;
  }

  function canvasEncodeJpeg(bitmap, quality) {
    var w = bitmap.width || bitmap.videoWidth || 0;
    var h = bitmap.height || bitmap.videoHeight || 0;
    if (!w || !h) return Promise.reject(new Error('heic_decode_empty'));
    var canvas = document.createElement('canvas');
    canvas.width = w;
    canvas.height = h;
    var ctx = canvas.getContext('2d');
    ctx.drawImage(bitmap, 0, 0);
    if (bitmap.close) {
      try {
        bitmap.close();
      } catch (e) {}
    }
    return new Promise(function (resolve, reject) {
      canvas.toBlob(
        function (blob) {
          if (!blob) reject(new Error('jpeg_encode_failed'));
          else resolve(blob);
        },
        'image/jpeg',
        quality == null ? 0.92 : quality
      );
    });
  }

  function convertHeicToJpeg(file, heic2anyUrl) {
    // Prefer native decode when the browser can open HEIC (newer iOS Safari).
    var native = Promise.resolve()
      .then(function () {
        if (typeof createImageBitmap === 'function') return createImageBitmap(file);
        return Promise.reject(new Error('no_createImageBitmap'));
      })
      .then(function (bitmap) {
        return canvasEncodeJpeg(bitmap, 0.92);
      });

    return native.catch(function () {
      return loadHeic2Any(heic2anyUrl).then(function (heic2any) {
        return heic2any({ blob: file, toType: 'image/jpeg', quality: 0.92 }).then(function (out) {
          return Array.isArray(out) ? out[0] : out;
        });
      });
    }).then(function (blob) {
      var base = String(file.name || 'photo').replace(/\.[^.]+$/, '');
      var name = base + '.jpg';
      try {
        return new File([blob], name, { type: 'image/jpeg', lastModified: Date.now() });
      } catch (e) {
        // Older WebViews: Blob is enough for FormData upload.
        blob.name = name;
        return blob;
      }
    });
  }

  function walkGpoElements(els, fn) {
    if (!els) return;
    for (var i = 0; i < els.length; i++) {
      var el = els[i];
      if (!el) continue;
      if (el.type === 'group') walkGpoElements(el.elements, fn);
      else fn(el);
    }
  }

  function relaxGloboFileRequirements() {
    if (!window.GPOConfigs || !window.GPOConfigs.options) return;
    Object.keys(window.GPOConfigs.options).forEach(function (id) {
      var cfg = window.GPOConfigs.options[id];
      if (!cfg) return;
      walkGpoElements(cfg.elements, function (el) {
        if (el && el.type === 'file') el.required = false;
      });
    });
  }

  function isFileOnlyRoot(node) {
    if (!node || node.nodeType !== 1) return false;
    if (node.getAttribute('data-type') === 'file') return true;
    var eid = node.getAttribute('data-element-id') || '';
    if (/^file-\d+/i.test(eid)) return true;
    // Prefer the smallest wrapper that contains a file input and no non-file fields.
    if (!node.querySelector('input[type="file"]')) return false;
    if (node.querySelector('input[type="text"], textarea, input[type="checkbox"], input[type="radio"]')) return false;
    return true;
  }

  function findGloboFileRoots() {
    var roots = [];
    var seen = typeof WeakSet !== 'undefined' ? new WeakSet() : null;

    function add(node) {
      if (!node || node.nodeType !== 1) return;
      if (!isFileOnlyRoot(node)) {
        // Walk down to a file-only child if this is a mixed group.
        var child =
          node.querySelector('[data-type="file"], [data-element-id^="file-"]') ||
          (node.querySelector('input[type="file"]') && node.querySelector('input[type="file"]').closest('[data-element-id], [data-type], .gpo-ov-item'));
        if (child && child !== node) {
          add(child);
          return;
        }
        if (!node.querySelector('input[type="file"]')) return;
      }
      if (seen) {
        if (seen.has(node)) return;
        seen.add(node);
      }
      roots.push(node);
    }

    document.querySelectorAll('[data-type="file"], [data-element-id^="file-"]').forEach(function (el) {
      add(el);
    });

    document.querySelectorAll('input[type="file"]').forEach(function (el) {
      if (el.closest('fondamia-customizer')) return;
      var wrap =
        el.closest('[data-type="file"], [data-element-id^="file-"], .gpo-ov-item') ||
        el.parentElement;
      add(wrap);
    });

    return roots;
  }

  function hideGloboFileOptions() {
    relaxGloboFileRequirements();
    var roots = findGloboFileRoots();
    roots.forEach(function (root) {
      // Idempotent: avoid rewriting DOM every mutation (was freezing the page with Globo).
      if (root.getAttribute('data-fondamia-hidden-file') === 'true') {
        root.querySelectorAll('input, select, textarea, button').forEach(function (field) {
          if (field.required) {
            field.required = false;
            field.removeAttribute('required');
          }
          if (!field.classList.contains('gpoExcludeValidate')) {
            field.classList.add('gpoExcludeValidate');
          }
        });
        return;
      }
      root.classList.add('fondamia-hide-globo-file', 'gpoExcludeValidate');
      root.setAttribute('data-fondamia-hidden-file', 'true');
      root.style.cssText =
        'display:none!important;visibility:hidden!important;pointer-events:none!important;position:absolute!important;width:0!important;height:0!important;overflow:hidden!important;';
      root.querySelectorAll('input, select, textarea, button').forEach(function (field) {
        field.classList.add('gpoExcludeValidate');
        if (field.required) field.required = false;
        field.removeAttribute('required');
        field.setAttribute('aria-hidden', 'true');
        field.tabIndex = -1;
        field.style.pointerEvents = 'none';
      });
    });
    document.documentElement.classList.add('fondamia-has-customizer');
  }

  function syncGloboFileValue(label, url, opts) {
    if (!label) return;
    var emit = !!(opts && opts.emit);
    var next = url || '';
    var name = 'properties[' + label + ']';
    var nodes = document.querySelectorAll('[name="' + name.replace(/"/g, '\\"') + '"]');
    for (var i = 0; i < nodes.length; i++) {
      var el = nodes[i];
      if (el.closest('fondamia-customizer')) continue;
      if (el.value === next) continue;
      el.value = next;
      if (!emit) continue;
      try {
        el.dispatchEvent(new Event('input', { bubbles: true }));
        el.dispatchEvent(new Event('change', { bubbles: true }));
      } catch (e) {}
    }

    // Some Globo builds store the URL on a sibling/hidden field near the file option.
    document.querySelectorAll('[data-type="file"][data-fondamia-hidden-file], [data-fondamia-hidden-file]').forEach(function (root) {
      var title = (root.textContent || '').replace(/\s+/g, ' ');
      if (title.indexOf(label) === -1) return;
      root.querySelectorAll('input[type="hidden"], input[type="text"], input[type="url"]').forEach(function (inp) {
        if (inp.value !== next) inp.value = next;
      });
    });
  }

  function watchGloboRender(cb) {
    var running = false;
    var scheduled = null;
    var finished = false;
    var run = function () {
      if (finished || running) return;
      running = true;
      try {
        cb();
      } finally {
        running = false;
      }
    };
    var schedule = function () {
      if (finished || scheduled) return;
      scheduled = setTimeout(function () {
        scheduled = null;
        run();
      }, 250);
    };

    run();
    ['gpoRenderCompleted', 'globoFilterRenderCompleted'].forEach(function (ev) {
      document.addEventListener(ev, schedule);
      window.addEventListener(ev, schedule);
    });

    var tries = 0;
    var timer = setInterval(function () {
      tries += 1;
      schedule();
      if (tries >= 8) clearInterval(timer);
    }, 1000);

    if (window.MutationObserver && document.body) {
      var target =
        document.querySelector('.gpo-app, #gpo-app, .gpo-container, [id*="gpo"], [class*="globo"]') ||
        document.body;
      var obs = new MutationObserver(schedule);
      obs.observe(target, { childList: true, subtree: true });
      setTimeout(function () {
        finished = true;
        obs.disconnect();
        if (scheduled) {
          clearTimeout(scheduled);
          scheduled = null;
        }
        clearInterval(timer);
      }, 6000);
    }
  }

  class FondamiaCustomizer extends HTMLElement {
    connectedCallback() {
      if (this._init) return;
      this._init = true;
      this.uid = ++UID;

      this.config = {
        policyUrl: this.dataset.policyUrl || '',
        aiPreviewUrl: this.dataset.aiPreviewUrl || '',
        previewMode: (this.dataset.previewMode || 'instant').toLowerCase(),
        productType: (this.dataset.productType || 'portrait').toLowerCase(),
        uploadKey: this.dataset.uploadKey || "Upload your pet's photo",
        secondKey: this.dataset.secondKey || 'Second pet photo',
        aiKey: this.dataset.aiKey || 'Product preview',
        nameKey: this.dataset.nameKey || "Pet's name",
        dateKey: this.dataset.dateKey || 'Memorial date',
        styleKey: this.dataset.styleKey || 'Style',
        bgKey: this.dataset.bgKey || 'Background',
        frameKey: this.dataset.frameKey || 'Frame',
        additionalKey: this.dataset.additionalKey || 'Additional pet',
        enableSecondPhoto: this.dataset.enableSecondPhoto === 'true',
        maxSize: parseInt(this.dataset.maxSize || '20971520', 10),
        heic2anyUrl: this.dataset.heic2anyUrl || '',
      };

      this.photos = [null, null];
      this.uploadedUrls = ['', ''];
      this.objectUrls = [null, null];
      this.aiPreviewUrl = '';
      this.genCount = 0;
      this.generating = false;

      this.previewImg = this.querySelector('.fondamia-customizer__preview-img');
      this.previewZoomBtn = this.querySelector('.fondamia-customizer__preview-zoom');
      this.lightbox = this.querySelector('.fondamia-customizer__lightbox');
      this.lightboxImg = this.querySelector('.fondamia-customizer__lightbox-img');
      this.statusEl = this.querySelector('.fondamia-customizer__status');
      this.progressEl = this.querySelector('.fondamia-customizer__progress');
      this.progressBar = this.querySelector('.fondamia-customizer__progress-bar');
      this.generateBtn = this.querySelector('.fondamia-customizer__generate');

      this.form =
        this.closest('form') ||
        document.querySelector('product-form form, form[data-type="add-to-cart-form"], form[action*="/cart/add"]');

      this.ensureHiddenInputs();
      this.bindUploaders();
      this.bindGenerate();
      this.bindPreviewZoom();
      this.bindFormWatch();
      this.updateGenerateState();

      watchGloboRender(function () {
        hideGloboFileOptions();
      });
    }

    ensureHiddenInputs() {
      this.hiddenMain = this.makeHidden(this.config.uploadKey);
      this.hiddenSecond = this.makeHidden(this.config.secondKey);
      this.hiddenAi = this.makeHidden(this.config.aiKey);
    }

    makeHidden(key) {
      if (!this.form) return null;
      var name = 'properties[' + key + ']';
      var existing = this.findByName(this.form, name);
      if (existing) return existing;
      var input = document.createElement('input');
      input.type = 'hidden';
      input.name = name;
      input.value = '';
      this.form.appendChild(input);
      return input;
    }

    findByName(root, name) {
      var els = root.querySelectorAll('[name]');
      for (var i = 0; i < els.length; i++) {
        if (els[i].getAttribute('name') === name) return els[i];
      }
      return null;
    }

    selectionRoot() {
      return (
        this.closest('product-info, product-component, .product, section') ||
        document
      );
    }

    normalizeOptionName(name) {
      return String(name || '')
        .replace(/\s+/g, ' ')
        .trim()
        .toLowerCase()
        .replace(/^options\[(.*)\]$/, '$1')
        .replace(/[-\s]+\d+$/, '')
        .trim();
    }

    optionNameMatches(elName, wanted) {
      var a = this.normalizeOptionName(elName);
      var b = this.normalizeOptionName(wanted);
      return a === b || a.indexOf(b) === 0 || b.indexOf(a) === 0;
    }

    readProp(key) {
      if (!key) return '';
      var name = 'properties[' + key + ']';
      var root = this.selectionRoot();
      var els = root.querySelectorAll('[name]');
      var value = '';
      for (var i = 0; i < els.length; i++) {
        var el = els[i];
        if (el.getAttribute('name') !== name) continue;
        if (el.type === 'radio' || el.type === 'checkbox') {
          if (el.checked) value = el.value;
        } else {
          value = el.value;
        }
      }
      return (value || '').trim();
    }

    readPropAny(keys) {
      for (var i = 0; i < keys.length; i++) {
        var v = this.readProp(keys[i]);
        if (v) return v;
      }
      return '';
    }

    readNamedControl(names) {
      var root = this.selectionRoot();
      var radios = root.querySelectorAll('input[type="radio"]:checked');
      for (var i = 0; i < names.length; i++) {
        var wanted = names[i];
        for (var r = 0; r < radios.length; r++) {
          var el = radios[r];
          if (!this.optionNameMatches(el.getAttribute('name'), wanted)) continue;
          if (el.value) return String(el.value).trim();
        }
        var selects = root.querySelectorAll('select');
        for (var s = 0; s < selects.length; s++) {
          var sel = selects[s];
          if (!this.optionNameMatches(sel.getAttribute('name'), wanted)) continue;
          if (sel.value) return String(sel.value).trim();
        }
      }
      return '';
    }

    readFieldsetOption(labelNeedles) {
      var root = this.selectionRoot();
      var fieldsets = root.querySelectorAll('fieldset');
      for (var i = 0; i < fieldsets.length; i++) {
        var fs = fieldsets[i];
        var legend = (fs.querySelector('legend') || {}).textContent || '';
        var L = legend.replace(/\s+/g, ' ').trim().toLowerCase();
        var hit = false;
        for (var j = 0; j < labelNeedles.length; j++) {
          if (L.indexOf(String(labelNeedles[j]).toLowerCase()) !== -1) {
            hit = true;
            break;
          }
        }
        if (!hit) continue;
        var checked = fs.querySelector('input[type="radio"]:checked, input[type="checkbox"]:checked');
        if (checked) {
          var lab = fs.querySelector('label[for="' + checked.id + '"]');
          var txt = (checked.value || (lab && lab.textContent) || '').trim();
          return String(txt).replace(/\s+/g, ' ').trim();
        }
        var sel = fs.querySelector('select');
        if (sel && sel.value) return String(sel.value).trim();
      }
      return '';
    }

    readVariantTitle() {
      try {
        var root = this.selectionRoot();
        var idEl =
          (this.form && this.form.querySelector('[name="id"]')) ||
          root.querySelector('[name="id"]');
        var id = idEl ? String(idEl.value || '') : '';
        var jsonEl =
          root.querySelector('[type="application/json"][data-product-json], script[data-product]') ||
          document.querySelector('script[type="application/json"][data-product-json]');
        if (jsonEl && id) {
          var data = parseJSON(jsonEl.textContent, null);
          var variants = (data && (data.variants || (data.product && data.product.variants))) || [];
          for (var i = 0; i < variants.length; i++) {
            if (String(variants[i].id) === id) return variants[i].title || variants[i].name || '';
          }
        }
        if (window.ShopifyAnalytics && window.ShopifyAnalytics.meta && window.ShopifyAnalytics.meta.selectedVariant) {
          return window.ShopifyAnalytics.meta.selectedVariant.name || '';
        }
      } catch (e) {}
      return '';
    }

    collectSelections() {
      // Shopify Online Store 2.0 uses names like "Format-1 " / "Style-2 " outside the <form>.
      var style =
        this.readNamedControl(['Style']) ||
        this.readFieldsetOption(['style']) ||
        this.readPropAny([this.config.styleKey, 'Style', 'Art style', 'Portrait style']);
      var background =
        this.readNamedControl(['Background']) ||
        this.readFieldsetOption(['background']) ||
        this.readPropAny([this.config.bgKey, 'Background', 'Backdrop']);
      var frame =
        this.readNamedControl(['Frame', 'Frame color']) ||
        this.readFieldsetOption(['frame color', 'frame']) ||
        this.readPropAny([this.config.frameKey, 'Frame', 'Frame color', 'Frame style']);
      var format =
        this.readNamedControl(['Format']) ||
        this.readFieldsetOption(['format']) ||
        this.readPropAny(['Format']);
      var size =
        this.readNamedControl(['Size']) ||
        this.readFieldsetOption(['size']) ||
        this.readPropAny(['Size']);
      var shape =
        this.readNamedControl(['Shape']) ||
        this.readFieldsetOption(['shape']) ||
        this.readPropAny(['Shape']);
      var collarWidth =
        this.readNamedControl(['Collar width', 'Collar Width']) ||
        this.readFieldsetOption(['collar']) ||
        this.readPropAny(['Collar width', 'Collar Width']);
      var frameColor =
        this.readNamedControl(['Frame color', 'Frame Color']) ||
        this.readFieldsetOption(['frame color']) ||
        frame;
      var name =
        this.readPropAny([this.config.nameKey, "Pet's name", 'Pet name', 'Name']) ||
        this.readNamedControl(["Pet's name", 'Pet name', 'Name']);
      var date = this.readPropAny([
        this.config.dateKey,
        'Memorial date',
        'Date',
        'Year / Date',
        'Dates',
      ]);
      var variantTitle = this.readVariantTitle();

      // Variant titles like "Digital / Classic Oil" or 'S - 4" / Soft Sky'
      if (variantTitle) {
        var parts = variantTitle.split(/\s*\/\s*/);
        if (parts.length >= 2) {
          if (!format) format = parts[0];
          if (!style && /watercolor|oil|minimal|floral/i.test(parts[1])) style = parts[1];
          if (!size && /^[sml]\b|\d|"|in\b/i.test(parts[0])) size = parts[0];
          if (!background && /sky|floral|rainbow|stained|watercolor|neutral|soft/i.test(parts[1])) {
            background = parts[1];
          }
          if (!frameColor && /wood|black|platinum/i.test(parts[1])) frameColor = parts[1];
          if (!collarWidth && /standard|wide|inch|"|in\b/i.test(parts[0])) collarWidth = parts[0];
        } else if (parts.length === 1) {
          if (!shape && /heart|round/i.test(parts[0])) shape = parts[0];
          if (!size && /^[sml]\b|\d|"|in\b/i.test(parts[0])) size = parts[0];
        }
        if (!shape && /heart|round/i.test(variantTitle)) shape = variantTitle;
      }

      // Format encodes framed vs digital/print for portrait.
      if (!frame && format) {
        if (/framed/i.test(format)) frame = 'Framed';
        else if (/digital/i.test(format)) frame = 'Digital';
        else if (/print/i.test(format)) frame = 'Unframed print';
      }

      // Product-specific fallbacks so each page always sends meaningful options.
      var ptype = this.config.productType || 'portrait';
      if (ptype === 'suncatcher') {
        if (!size) size = this.readFieldsetOption(['size']) || size;
        if (!background) background = this.readFieldsetOption(['background']) || background;
      } else if (ptype === 'collar-frame') {
        if (!collarWidth) collarWidth = this.readFieldsetOption(['collar']) || collarWidth;
        if (!frameColor) frameColor = this.readFieldsetOption(['frame color', 'frame']) || frameColor;
      } else if (ptype === 'ornament') {
        if (!shape) shape = this.readFieldsetOption(['shape']) || shape;
        if (!background) {
          background =
            this.readPropAny(['Background', 'Backdrop', 'Photo background']) || background;
        }
      }

      return {
        productType: ptype,
        style: style,
        background: background,
        frame: frame,
        format: format,
        size: size,
        shape: shape,
        collarWidth: collarWidth,
        frameColor: frameColor,
        name: name,
        date: date,
        variantTitle: variantTitle,
      };
    }

    bindUploaders() {
      var self = this;
      this.querySelectorAll('.fondamia-customizer__uploader').forEach(function (zone) {
        var slot = parseInt(zone.dataset.slot || '0', 10);
        var input = zone.querySelector('input[type="file"]');
        var chooseBtn = zone.querySelector('.fondamia-customizer__choose');
        if (!input) return;

        input.addEventListener('change', function () {
          if (input.files && input.files[0]) self.handleFile(slot, input.files[0]);
        });

        // Prefer native <label for="..."> activation. Keep JS fallback if label is blocked.
        if (chooseBtn && chooseBtn.tagName !== 'LABEL') {
          chooseBtn.addEventListener('click', function (e) {
            e.preventDefault();
            e.stopPropagation();
            try {
              input.click();
            } catch (err) {
              if (window.console) console.error('[fondamia] file open failed', err);
            }
          });
        }

        ['dragenter', 'dragover'].forEach(function (ev) {
          zone.addEventListener(ev, function (e) {
            e.preventDefault();
            e.stopPropagation();
            zone.classList.add('is-dragover');
          });
        });
        ['dragleave', 'drop'].forEach(function (ev) {
          zone.addEventListener(ev, function (e) {
            e.preventDefault();
            e.stopPropagation();
            zone.classList.remove('is-dragover');
          });
        });
        zone.addEventListener('drop', function (e) {
          var dt = e.dataTransfer;
          if (dt && dt.files && dt.files[0]) self.handleFile(slot, dt.files[0]);
        });
      });
    }

    handleFile(slot, file) {
      var self = this;
      var fmt = detectFormat(file);
      if (!fmt) {
        this.setStatus('Unsupported format. Please upload JPG, PNG, or HEIC/HEIF.', 'error');
        return;
      }
      if (file.size > this.config.maxSize) {
        this.setStatus('Image is too large. Max ' + Math.round(this.config.maxSize / 1048576) + 'MB.', 'error');
        return;
      }

      var ready = Promise.resolve(file);
      if (isHeicFormat(fmt)) {
        this.setStatus('Converting HEIC photo for upload\u2026', 'uploading');
        this.setProgress(8, true);
        ready = convertHeicToJpeg(file, this.config.heic2anyUrl).catch(function (err) {
          if (window.console) console.error('[fondamia] heic convert', err);
          throw new Error('heic_convert_failed');
        });
      }

      ready
        .then(function (normalized) {
          if (normalized.size > self.config.maxSize) {
            self.setStatus('Image is too large. Max ' + Math.round(self.config.maxSize / 1048576) + 'MB.', 'error');
            self.setProgress(0, false);
            return;
          }
          self.photos[slot] = normalized;
          if (self.objectUrls[slot]) URL.revokeObjectURL(self.objectUrls[slot]);
          self.objectUrls[slot] = URL.createObjectURL(normalized);

          if (slot === 0) {
            self.aiPreviewUrl = '';
            self.showPreview(self.objectUrls[0], false);
          }

          self.updateThumb(slot);
          self.upload(slot, normalized);
          self.updateGenerateState();
        })
        .catch(function () {
          self.setProgress(0, false);
          self.setStatus('Could not read this HEIC photo. Please export as JPG and try again.', 'error');
        });
    }

    showPreview(url, isAi) {
      if (!this.previewImg) return;
      if (url) {
        this.previewImg.src = url;
        this.previewImg.hidden = false;
        if (this.previewZoomBtn) this.previewZoomBtn.hidden = false;
        this.setAttribute('data-has-image', 'true');
        this.setAttribute('data-ai-preview', isAi ? 'true' : 'false');
      } else {
        this.previewImg.removeAttribute('src');
        this.previewImg.hidden = true;
        if (this.previewZoomBtn) this.previewZoomBtn.hidden = true;
        this.closeLightbox();
        this.removeAttribute('data-has-image');
        this.removeAttribute('data-ai-preview');
      }
    }

    bindPreviewZoom() {
      var self = this;
      if (this.previewZoomBtn) {
        this.previewZoomBtn.addEventListener('click', function () {
          self.openLightbox();
        });
      }
      if (!this.lightbox) return;
      var closeBtn = this.lightbox.querySelector('.fondamia-customizer__lightbox-close');
      var backdrop = this.lightbox.querySelector('.fondamia-customizer__lightbox-backdrop');
      if (closeBtn) closeBtn.addEventListener('click', function () { self.closeLightbox(); });
      if (backdrop) backdrop.addEventListener('click', function () { self.closeLightbox(); });
      this._onLightboxKey = function (e) {
        if (e.key === 'Escape') self.closeLightbox();
      };
    }

    openLightbox() {
      if (!this.lightbox || !this.previewImg || !this.previewImg.src) return;
      if (this.lightboxImg) {
        this.lightboxImg.src = this.previewImg.src;
        this.lightboxImg.alt = this.previewImg.alt || 'Enlarged preview';
      }
      // Move to body so product-column overflow cannot clip the overlay.
      if (this.lightbox.parentElement !== document.body) {
        this._lightboxHome = this.lightbox.parentElement;
        document.body.appendChild(this.lightbox);
      }
      this.lightbox.hidden = false;
      document.body.classList.add('fondamia-lightbox-open');
      document.addEventListener('keydown', this._onLightboxKey);
      var closeBtn = this.lightbox.querySelector('.fondamia-customizer__lightbox-close');
      if (closeBtn) {
        try {
          closeBtn.focus();
        } catch (e) {}
      }
    }

    closeLightbox() {
      if (!this.lightbox || this.lightbox.hidden) return;
      this.lightbox.hidden = true;
      document.body.classList.remove('fondamia-lightbox-open');
      document.removeEventListener('keydown', this._onLightboxKey);
      if (this.lightboxImg) this.lightboxImg.removeAttribute('src');
      if (this._lightboxHome && this.lightbox.parentElement === document.body) {
        this._lightboxHome.appendChild(this.lightbox);
      }
    }

    updateThumb(slot) {
      var zone = this.querySelector('.fondamia-customizer__uploader[data-slot="' + slot + '"]');
      if (!zone) return;
      var thumbs = zone.parentElement.querySelector('.fondamia-customizer__thumbs[data-slot="' + slot + '"]');
      if (!thumbs) return;
      thumbs.innerHTML = '';
      if (!this.objectUrls[slot]) return;
      var wrap = document.createElement('div');
      wrap.className = 'fondamia-customizer__thumb';
      var im = document.createElement('img');
      im.src = this.objectUrls[slot];
      wrap.appendChild(im);
      var rm = document.createElement('button');
      rm.type = 'button';
      rm.className = 'fondamia-customizer__thumb-remove';
      rm.textContent = '\u00d7';
      var self = this;
      rm.addEventListener('click', function (e) {
        e.stopPropagation();
        self.clearSlot(slot);
      });
      wrap.appendChild(rm);
      thumbs.appendChild(wrap);
    }

    clearSlot(slot) {
      this.photos[slot] = null;
      this.uploadedUrls[slot] = '';
      if (this.objectUrls[slot]) {
        URL.revokeObjectURL(this.objectUrls[slot]);
        this.objectUrls[slot] = null;
      }
      this.updateThumb(slot);
      this.syncHidden({ emit: true });
      if (slot === 0) {
        this.aiPreviewUrl = '';
        this.showPreview('', false);
      }
      this.updateGenerateState();
    }

    upload(slot, file) {
      var self = this;
      if (!this.config.policyUrl) {
        this.setStatus('Upload endpoint not configured yet.', 'error');
        return;
      }
      this.setStatus('Uploading your photo\u2026', 'uploading');
      this.setProgress(0, true);

      fetch(this.config.policyUrl, { method: 'GET' })
        .then(function (r) {
          if (!r.ok) throw new Error('policy ' + r.status);
          return r.json();
        })
        .then(function (p) {
          var ext = (file.name.split('.').pop() || 'jpg').toLowerCase().replace(/[^a-z0-9]/g, '');
          var key = p.dir + Date.now() + '-' + Math.random().toString(36).slice(2, 10) + '.' + ext;
          var fd = new FormData();
          fd.append('key', key);
          fd.append('OSSAccessKeyId', p.accessKeyId);
          fd.append('policy', p.policy);
          fd.append('Signature', p.signature);
          fd.append('success_action_status', '200');
          fd.append('Content-Type', file.type);
          fd.append('file', file);

          var xhr = new XMLHttpRequest();
          xhr.open('POST', p.host, true);
          xhr.upload.onprogress = function (e) {
            if (e.lengthComputable) self.setProgress(Math.round((e.loaded / e.total) * 100), true);
          };
          xhr.onload = function () {
            if (xhr.status >= 200 && xhr.status < 300) {
              self.uploadedUrls[slot] = p.host + '/' + key;
              self.syncHidden({ emit: true });
              self.setProgress(100, false);
              if (self.config.previewMode === 'instant' && slot === 0) {
                self.showPreview(self.objectUrls[0] || self.uploadedUrls[0], false);
                self.setStatus('Photo uploaded \u2713 Preview ready', 'done');
              } else {
                self.setStatus('Photo uploaded \u2713', 'done');
              }
              self.updateGenerateState();
              // AI mockup: wait for the shopper to click Generate (do not auto-start).
              if (self.config.previewMode === 'ai' && slot === 0 && !self.aiPreviewUrl) {
                self.setStatus('Photo uploaded \u2713 Click Generate to create your preview', 'done');
              }
            } else {
              self.setProgress(0, false);
              self.setStatus('Upload failed (' + xhr.status + '). Please try again.', 'error');
            }
          };
          xhr.onerror = function () {
            self.setProgress(0, false);
            self.setStatus('Upload failed (network). Please try again.', 'error');
          };
          xhr.send(fd);
        })
        .catch(function (err) {
          self.setProgress(0, false);
          var msg = 'Could not start upload. Please try again.';
          var detail = err && err.message ? String(err.message) : '';
          if (detail.indexOf('Failed to fetch') !== -1 || detail.indexOf('NetworkError') !== -1) {
            msg = 'Upload server unreachable. Check the signing API is running.';
          } else if (detail.indexOf('policy ') === 0) {
            msg = 'Upload signing failed (' + detail.slice(7) + ').';
          }
          self.setStatus(msg, 'error');
          if (window.console) console.error('[fondamia] policy error', err);
        });
    }

    bindGenerate() {
      var self = this;
      if (!this.generateBtn) return;
      this.generateBtn.addEventListener('click', function () {
        self.generateAiPreview();
      });
    }

    updateGenerateState() {
      if (!this.generateBtn) return;
      var ready = !!this.uploadedUrls[0] && !!this.config.aiPreviewUrl && !this.generating;
      var limited = this.genCount >= SESSION_LIMIT;
      this.generateBtn.disabled = !ready || limited;
      if (limited) {
        this.generateBtn.textContent = this.generateBtn.dataset.limitLabel || 'Preview limit reached';
      } else if (this.generating) {
        this.generateBtn.textContent = this.generateBtn.dataset.busyLabel || 'Generating\u2026';
      } else {
        this.generateBtn.textContent = this.generateBtn.dataset.label || 'Generate product preview';
      }
    }

    generateAiPreview() {
      var self = this;
      if (!this.config.aiPreviewUrl) {
        this.setStatus('Preview endpoint not configured.', 'error');
        return;
      }
      if (!this.uploadedUrls[0]) {
        this.setStatus('Upload a photo first.', 'error');
        return;
      }
      if (this.genCount >= SESSION_LIMIT) {
        this.setStatus('Preview limit reached for this session.', 'error');
        return;
      }
      if (this.generating) return;

      this.generating = true;
      this.genCount += 1;
      this.updateGenerateState();
      this.setAttribute('data-generating', 'true');
      this.setStatus('Generating product preview\u2026 this can take 20\u201340 seconds.', 'uploading');
      this.setProgress(30, true);

      var selections = this.collectSelections();
      if (window.console) console.log('[fondamia] preview selections', selections);
      var selHint = [selections.format, selections.style, selections.background, selections.shape, selections.frameColor || selections.frame]
        .filter(Boolean)
        .join(' · ');
      if (selHint) {
        this.setStatus('Generating product preview (' + selHint + ')\u2026 20\u201340s', 'uploading');
      }

      fetch(this.config.aiPreviewUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          imageUrl: this.uploadedUrls[0],
          secondImageUrl: this.uploadedUrls[1] || '',
          productType: selections.productType,
          style: selections.style,
          background: selections.background,
          frame: selections.frame,
          format: selections.format,
          size: selections.size,
          shape: selections.shape,
          collarWidth: selections.collarWidth,
          frameColor: selections.frameColor,
          name: selections.name,
          date: selections.date,
          variantTitle: selections.variantTitle,
        }),
      })
        .then(function (r) {
          return r.json().then(function (j) {
            return { ok: r.ok, status: r.status, body: j };
          });
        })
        .then(function (res) {
          self.generating = false;
          self.removeAttribute('data-generating');
          self.setProgress(0, false);
          self.updateGenerateState();
          if (!res.ok || !res.body || !res.body.previewUrl) {
            var msg = 'Preview failed';
            if (res.status === 429) msg = 'Too many previews. Please try again later.';
            else if (res.body && res.body.error === 'jbb_failed') msg = 'Preview service is busy. Please try again in a moment.';
            else if (res.body && res.body.error) msg = 'Preview failed (' + res.body.error + ').';
            self.setStatus(msg, 'error');
            return;
          }
          self.aiPreviewUrl = res.body.previewUrl;
          self.showPreview(self.aiPreviewUrl, true);
          self.syncHidden({ emit: true });
          self.setStatus('Preview ready \u2713', 'done');
          if (window.console) {
            console.log('[fondamia] preview ready', {
              previewUrl: self.aiPreviewUrl,
              petUrl: self.uploadedUrls[0],
              via: res.body.via,
              model: res.body.model,
            });
          }
        })
        .catch(function (err) {
          self.generating = false;
          self.removeAttribute('data-generating');
          self.setProgress(0, false);
          self.updateGenerateState();
          self.setStatus('Preview failed (network). Please try again.', 'error');
          if (window.console) console.error('[fondamia] preview', err);
        });
    }

    syncHidden(opts) {
      if (this.hiddenMain) this.hiddenMain.value = this.uploadedUrls[0] || '';
      if (this.hiddenSecond) this.hiddenSecond.value = this.uploadedUrls[1] || '';
      if (this.hiddenAi) this.hiddenAi.value = this.aiPreviewUrl || '';
      // Never emit Globo events during watch/hide passes — that re-rendered Globo and froze the page.
      var emit = !!(opts && opts.emit);
      syncGloboFileValue(this.config.uploadKey, this.uploadedUrls[0] || '', { emit: emit });
      syncGloboFileValue(this.config.secondKey, this.uploadedUrls[1] || '', { emit: emit });
      syncGloboFileValue('Pet photo', this.uploadedUrls[0] || '', { emit: emit });
    }

    setStatus(msg, state) {
      if (!this.statusEl) return;
      this.statusEl.textContent = msg;
      this.statusEl.setAttribute('data-state', state || '');
    }

    setProgress(pct, active) {
      if (this.progressEl) this.progressEl.classList.toggle('is-active', !!active);
      if (this.progressBar) this.progressBar.style.width = pct + '%';
    }

    bindFormWatch() {
      if (!this.form) return;
      var self = this;
      var timer = null;
      var handler = function () {
        if (timer) return;
        timer = setTimeout(function () {
          timer = null;
          self.toggleSecondUploader();
          self.updateGenerateState();
        }, 50);
      };
      this.form.addEventListener('input', handler);
      this.form.addEventListener('change', handler);
      // Debounced, attributes-only observer — full childList watching fought Globo re-renders.
      if (window.MutationObserver) {
        this._observer = new MutationObserver(handler);
        this._observer.observe(this.form, {
          subtree: true,
          attributes: true,
          attributeFilter: ['value', 'checked', 'selected'],
        });
      }
      this.toggleSecondUploader();
    }

    toggleSecondUploader() {
      if (!this.config.enableSecondPhoto) return;
      var zone = this.querySelector('.fondamia-customizer__uploader[data-slot="1"]');
      if (!zone) return;
      var additional = this.readProp(this.config.additionalKey);
      var on = additional && additional.toLowerCase() !== 'no' && additional !== '' && additional !== 'false';
      zone.hidden = !on;
      if (!on && this.photos[1]) this.clearSlot(1);
    }

    disconnectedCallback() {
      this.closeLightbox();
      if (this._observer) this._observer.disconnect();
      if (this.objectUrls[0]) URL.revokeObjectURL(this.objectUrls[0]);
      if (this.objectUrls[1]) URL.revokeObjectURL(this.objectUrls[1]);
    }
  }

  if (!customElements.get('fondamia-customizer')) {
    customElements.define('fondamia-customizer', FondamiaCustomizer);
  }
})();
